import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { api, unwrap } from '../api/client.js';
import { errorMessage } from '../errors.js';
import { Button, Card, Checkbox, Notice, TextField, buttonClass } from './ui.js';

/** Site settings: the public status page (T-70) on or off, its address, name and a note. */
export function StatusPageCard({ siteId }: { siteId: string }) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const key = ['site', siteId, 'status-page'];
  const settings = useQuery({
    queryKey: key,
    queryFn: () => unwrap(api.GET('/sites/{siteId}/status-page', { params: { path: { siteId } } })),
  });
  const [enabled, setEnabled] = useState(false);
  const [slug, setSlug] = useState('');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const messageId = useId();
  useEffect(() => {
    if (!settings.data) return;
    setEnabled(settings.data.enabled);
    setSlug(settings.data.slug);
    setTitle(settings.data.title ?? '');
    setMessage(settings.data.message ?? '');
  }, [settings.data]);
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        api.PUT('/sites/{siteId}/status-page', {
          params: { path: { siteId } },
          body: {
            enabled,
            slug: slug.trim().toLowerCase(),
            title: title.trim() || null,
            message: message.trim() || null,
          },
        }),
      ),
    onSuccess: (data) => client.setQueryData(key, data),
  });
  if (!settings.data) return null;
  const url = `${window.location.origin}${settings.data.path}`;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };

  return (
    <Card>
      <h2 className="mb-1 text-xl font-semibold">{t('statusPage.title')}</h2>
      <p className="mb-4 max-w-prose text-slate-700">{t('statusPage.intro')}</p>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Checkbox
          label={t('statusPage.enabled')}
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
        />
        <TextField
          label={t('statusPage.slug')}
          hint={t('statusPage.slugHint', { origin: `${window.location.origin}/status/` })}
          value={slug}
          maxLength={40}
          pattern="[a-z0-9][a-z0-9-]{1,38}[a-z0-9]"
          onChange={(e) => setSlug(e.target.value)}
        />
        <TextField
          label={t('statusPage.name')}
          hint={t('statusPage.nameHint')}
          optional
          maxLength={80}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <div className="flex flex-col gap-1">
          <label htmlFor={messageId} className="font-semibold">
            {t('statusPage.message')}{' '}
            <span className="font-normal text-slate-600">({t('app.optional')})</span>
          </label>
          <p className="text-sm text-slate-700">{t('statusPage.messageHint')}</p>
          <textarea
            id={messageId}
            value={message}
            maxLength={500}
            rows={3}
            onChange={(e) => setMessage(e.target.value)}
            className="rounded-md border border-slate-400 bg-white px-3 py-2 text-base"
          />
        </div>
        {!settings.data.logo ? (
          <p className="text-sm text-slate-700">{t('statusPage.logoPlan')}</p>
        ) : null}
        {save.isError ? <Notice kind="error">{errorMessage(save.error, t)}</Notice> : null}
        {save.isSuccess ? <Notice kind="success">{t('app.saved')}</Notice> : null}
        <div className="flex flex-wrap items-center gap-2">
          <Button type="submit" busy={save.isPending}>
            {t('app.save')}
          </Button>
          {settings.data.enabled ? (
            <a href={url} target="_blank" rel="noreferrer" className={buttonClass('soft')}>
              {t('statusPage.open')}
            </a>
          ) : null}
        </div>
        {settings.data.enabled ? (
          <p className="break-all text-sm text-slate-800">
            {t('statusPage.link')} <span className="font-mono">{url}</span>
          </p>
        ) : null}
      </form>
    </Card>
  );
}
